// src/App.js
import logo from './logo.svg';
import './App.css';
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

import SelectPlan from './pages/SelectPlan';
import CreateAccount from './pages/CreateAccount';
import Login from './pages/Login';
import Impersonate from './pages/Impersonate';
import ImpersonationBanner from './components/ImpersonationBanner';
import TrialLanding from './pages/TrialLanding';
import TrialSignup from './pages/TrialSignup';
import TrialStatus from './pages/TrialStatus';
import TrialEnroll from './pages/TrialEnroll';
import { TRIAL_SIGNUP_ENABLED } from './config/featureFlags';

import Home from './pages/Home';
import Chat from './pages/Chat';
import Games from './pages/Games';
import AlphabetTracingGame from './pages/games/AlphabetTracingGame';
import LetterSoundMatchGame from './pages/games/LetterSoundMatchGame';
import WordMatchGameV2 from './pages/games/WordMatchGameV2';
import AudioQuizGame from './pages/games/AudioQuizGame';
import ArabicHangman from './pages/games/ArabicHangman';
import ArabicFlagGuesser from './pages/games/ArabicFlagGuesser';
import ArabicNumbersSpeedGame from './pages/games/ArabicNumbersSpeedGame';
import ArabicNumberCardMatchingGame from './pages/games/ArabicNumberCardMatchingGame';
import ArabicNumberSequencingGame from './pages/games/ArabicNumberSequencingGame';
import ArabicNumberSumTargetGame from './pages/games/ArabicNumberSumTargetGame';
import ArabicMemoryCards from './pages/games/ArabicMemoryCards';
import BiggerSmallerCountryGame from './pages/games/BiggerSmallerCountryGame';
import MapExplorer from './pages/games/MapExplorer';
import ArabicNumberFishingGame from './pages/games/ArabicNumberFishingGame';

import Resources from './pages/Resources';
import Dashboard from './pages/Dashboard';
import AccountSettings from './pages/AccountSettings';
import AddChildren from './pages/AddChildren';
import CourseRenewal from './pages/CourseRenewal';
import CompleteSubscription from './pages/CompleteSubscription';
import PaymentSuccess from './pages/PaymentSuccess';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import TeacherRoute from './components/TeacherRoute';
import AdminDashboard from './pages/AdminDashboard';
import PrivacyPolicy from './pages/marketing/PrivacyPolicy';
import TermsOfService from './pages/marketing/TermsOfService';

import TeacherManager from './pages/admin/TeacherManager';
import ClassManager from './pages/admin/ClassManager';
import AttendanceManager from './pages/admin/AttendanceManager';
import RenewalManager from './pages/admin/RenewalManager';
import TrialManager from './pages/admin/TrialManager';
import ReferralCodeManager from './pages/admin/ReferralCodeManager';
import ResourceManager from './pages/admin/ResourceManager';
import TeacherResourceManager from './pages/admin/TeacherResourceManager';
import TeacherReports from './pages/admin/TeacherReports';
import FollowUpManager from './pages/admin/FollowUpManager';
import EmailTriggerManager from './pages/admin/EmailTriggerManager';
import TeacherPortal from './pages/teacher/TeacherPortal';

function App() {
  const { user, authLoading } = useAuth();

  if (authLoading) {
    return <div>Loading...</div>; // Optional: show a spinner or splash
  }

  return (
    <>
      <ImpersonationBanner />
      <Routes>
      <Route path="/select-plan" element={<SelectPlan />} />
      <Route path="/create-account" element={<CreateAccount />} />
      <Route path="/login" element={<Login />} />

      {/* Phase 1 public trial signup — feature-flagged, nothing links here yet.
          /t/:code records the referral code from the URL as attribution and
          forwards to /trial. The code is verified server-side by
          createTrialSignup, not on the landing page. */}
      {TRIAL_SIGNUP_ENABLED && <Route path="/t/:code" element={<TrialLanding />} />}
      {TRIAL_SIGNUP_ENABLED && <Route path="/trial" element={<TrialSignup />} />}
      {/* Standalone authed page — its own auth check, NOT a ProtectedRoute
          (the ProtectedRoute trial guard redirects here). */}
      {TRIAL_SIGNUP_ENABLED && <Route path="/trial-status" element={<TrialStatus />} />}
      {/* Trial conversion. Standalone + authed, NOT a ProtectedRoute: it must
          stay reachable for a trial-only parent (all children course:null), whom
          ProtectedRoute would bounce to /trial-status. Payment is never gated on
          trial status. */}
      {TRIAL_SIGNUP_ENABLED && <Route path="/enroll" element={<TrialEnroll />} />}
      <Route path="/impersonate" element={<Impersonate />} />
      <Route path="/complete-subscription" element={<CompleteSubscription />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />

      <Route path="/admin/attendance" element={<AdminRoute><AttendanceManager /></AdminRoute>} />
      <Route path="/admin/teachers" element={<AdminRoute><TeacherManager /></AdminRoute>} />
      <Route path="/admin/classes" element={<AdminRoute><ClassManager /></AdminRoute>} />
      <Route path="/admin/renewals" element={<AdminRoute><RenewalManager /></AdminRoute>} />
      <Route path="/admin/trials" element={<AdminRoute><TrialManager /></AdminRoute>} />
      <Route path="/admin/referral-codes" element={<AdminRoute><ReferralCodeManager /></AdminRoute>} />
      <Route path="/admin/resources" element={<AdminRoute><ResourceManager /></AdminRoute>} />
      <Route path="/admin/teacher-resources" element={<AdminRoute><TeacherResourceManager /></AdminRoute>} />
      <Route path="/admin/teacher-reports" element={<AdminRoute><TeacherReports /></AdminRoute>} />
      <Route path="/admin/followups" element={<AdminRoute><FollowUpManager /></AdminRoute>} />
      <Route path="/admin/email-triggers" element={<AdminRoute><EmailTriggerManager /></AdminRoute>} />

      <Route path="/teacher" element={<TeacherRoute><TeacherPortal /></TeacherRoute>} />


      <Route path="/" element={
        <ProtectedRoute>
          <Home />
        </ProtectedRoute>
      } />
      <Route path="/chat" element={
        <ProtectedRoute>
          <Chat />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/resources" element={
        <ProtectedRoute>
          <Resources />
        </ProtectedRoute>
      } />
      <Route path="/account" element={
        <ProtectedRoute>
          <AccountSettings />
        </ProtectedRoute>
      } />
      <Route path="/add-children" element={
        <ProtectedRoute>
          <AddChildren />
        </ProtectedRoute>
      } />
      <Route path="/course-renewal" element={
        <ProtectedRoute>
          <CourseRenewal />
        </ProtectedRoute>
      } />
      
      {/* Game Routes */}
      <Route path="/games" element={
        <ProtectedRoute>
          <Games />
        </ProtectedRoute>
      } />
      <Route path="/wordmatch" element={
        <ProtectedRoute>
          <WordMatchGameV2 />
        </ProtectedRoute>
      } />
      <Route path="/audioquiz" element={
        <ProtectedRoute>
          <AudioQuizGame />
        </ProtectedRoute>
      } /> 
      <Route path="/arabichangman" element={
        <ProtectedRoute>
          <ArabicHangman />
        </ProtectedRoute>
      } /> 
      <Route path="/arabicflagguesser" element={
        <ProtectedRoute>
          <ArabicFlagGuesser />
        </ProtectedRoute>
      } /> 
      <Route path="/arabicnumbersspeedgame" element={
        <ProtectedRoute>
          <ArabicNumbersSpeedGame />
        </ProtectedRoute>
      } />
      <Route path="/arabicnumbercardmatchinggame" element={
        <ProtectedRoute>
          <ArabicNumberCardMatchingGame />
        </ProtectedRoute>
      } />
      <Route path="/arabicnumbersequencinggame" element={
        <ProtectedRoute>
          <ArabicNumberSequencingGame />
        </ProtectedRoute>
      } />
      <Route path="/arabicnumbersumtargetgame" element={
        <ProtectedRoute>
          <ArabicNumberSumTargetGame />
        </ProtectedRoute>
      } />
      <Route path="/arabicnumberfishing" element={
        <ProtectedRoute>
          <ArabicNumberFishingGame />
        </ProtectedRoute>
      } />
      <Route path="/arabicmemorycards" element={
        <ProtectedRoute>
          <ArabicMemoryCards />
        </ProtectedRoute>
      } />
      <Route path="/biggersmallercountrygame" element={
        <ProtectedRoute>
          <BiggerSmallerCountryGame />
        </ProtectedRoute>
      } />
      <Route path="/mapexplorer" element={
        <ProtectedRoute>
          <MapExplorer />
        </ProtectedRoute>
      } />
      <Route path="/alphabettracing" element={
        <ProtectedRoute>
          <AlphabetTracingGame />
        </ProtectedRoute>
      } />
      <Route path="/lettersoundmatch" element={
        <ProtectedRoute>
          <LetterSoundMatchGame />
        </ProtectedRoute>
      } />
      <Route path="/privacy-policy" element={
        <PrivacyPolicy />} />
      <Route path="/terms-of-service" element={
        <TermsOfService />} />
      </Routes>
    </>
  );
}

export default App;